// src/pages/prodactions/master-data/MasterData.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, set, remove, push } from "firebase/database";
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimal,
    formatText
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function MasterData() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newData, setNewData] = useState({
        material_code: '',
        spec_code: '',
        description: '',
        class: '',
        size: '',
        berat_kemasan: ''
    });

    // State untuk animasi rotasi icon Plus/X
    const [isAddIconRotated, setIsAddIconRotated] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_productions/tb_master_data
                const masterDataRef = ref(database, "db_productions/tb_master_data");
                const masterDataSnapshot = await onValuePromise(masterDataRef);
                const masterData = masterDataSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (masterData) {
                    Object.keys(masterData).forEach(key => {
                        const record = masterData[key];
                        loadedData.push({ 
                            firebaseKey: key, 
                            material_code: record.material_code || '',
                            spec_code: record.spec_code || '',
                            description: record.description || '',
                            class: record.class || '',
                            size: record.size || '',
                            berat_kemasan: record.berat_kemasan || '',
                            last_updated: record.last_updated || null
                        });
                        
                        // Cari waktu update terakhir
                        if (record.last_updated) {
                            const updateTime = new Date(record.last_updated);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Sort by material_code
                loadedData.sort((a, b) => a.material_code.localeCompare(b.material_code));
                
                setOriginalDataList(loadedData);
                setDataList(loadedData);

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data master.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.material_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.spec_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.class.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // ADOPSI DARI UoMConfig.jsx: Fungsi import Excel
const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) {
        setSnackbar({
            open: true,
            message: "Tidak ada file yang dipilih.",
            severity: "warning",
        });
        return;
    }

    setImportLoading(true);
    setSnackbar({
        open: true,
        message: "Memulai proses import...",
        severity: "info",
    });

    try {
        // Baca file Excel
        const reader = new FileReader();
        
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // TAMPILKAN worksheet untuk debug
                console.log("Worksheet range:", worksheet["!ref"]);
                console.log("Worksheet keys:", Object.keys(worksheet).filter(k => k[0] !== '!'));
                
                // Opsi 1: Coba dengan raw: true untuk melihat data mentah
                const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });
                console.log("Raw data (header: 1):", rawData);
                
                // Opsi 2: Coba dengan default settings
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                console.log("JSON data (default):", jsonData);
                
                // Opsi 3: Coba dengan header: 'A' untuk mendapatkan semua kolom
                const jsonDataWithAllCols = XLSX.utils.sheet_to_json(worksheet, { header: 'A' });
                console.log("JSON data (header: 'A'):", jsonDataWithAllCols);

                // Path database
                const dbPath = "db_productions/tb_master_data";
                const tbRef = ref(database, dbPath);

                // Ambil data yang sudah ada
                const snapshot = await onValuePromise(tbRef);
                const existingData = snapshot.exists() ? snapshot.val() : {};

                // Buat array nilai material_code yang sudah ada
                const existingMaterialCodes = Object.values(existingData).map(item => 
                    item.material_code?.toLowerCase().trim()
                );

                let importedCount = 0;
                let updatedCount = 0;
                let ignoredCount = 0;
                const errors = [];

                // Gunakan data yang berhasil dibaca
                const dataToProcess = jsonData.length > 0 ? jsonData : 
                                    jsonDataWithAllCols.length > 0 ? jsonDataWithAllCols : 
                                    rawData.slice(1); // Skip header jika menggunakan header: 1

                console.log("Data yang akan diproses:", dataToProcess);
                console.log("Jumlah data:", dataToProcess.length);

                for (const item of dataToProcess) {
                    try {
                        // DEBUG: Tampilkan struktur item
                        console.log("Item saat diproses:", item);
                        console.log("Keys item:", Object.keys(item));
                        
                        // Cari semua kemungkinan nama kolom
                        let materialCode = "";
                        let specCode = "";
                        let description = "";
                        let classValue = "";
                        let size = "";
                        let beratKemasan = "";

                        // Cari berdasarkan berbagai kemungkinan nama kolom
                        if (typeof item === 'object') {
                            // Jika item adalah object dengan properties
                            materialCode = item["Material Code"] || item["material_code"] || item["Kode Material"] || 
                                         item["MaterialCode"] || item["materialcode"] || item["MATERIAL CODE"] || "";
                            
                            specCode = item["Spec Code"] || item["spec_code"] || item["Kode Spec"] || 
                                     item["SpecCode"] || item["speccode"] || item["SPEC CODE"] || "";
                            
                            description = item["Description"] || item["description"] || item["Deskripsi"] || 
                                        item["DESCRIPTION"] || "";
                            
                            classValue = item["Class"] || item["class"] || item["Kelas"] || 
                                       item["CLASS"] || "";
                            
                            size = item["Size"] || item["size"] || item["Ukuran"] || 
                                 item["SIZE"] || "";
                            
                            beratKemasan = item["Berat Kemasan"] || item["berat_kemasan"] || item["Berat"] || 
                                         item["BeratKemasan"] || item["beratkemasan"] || item["BERAT KEMASAN"] || "";
                        } else if (Array.isArray(item)) {
                            // Jika item adalah array (ketika menggunakan header: 1)
                            // Asumsi urutan kolom: Material Code, Spec Code, Description, Class, Size, Berat Kemasan
                            if (item.length >= 1) materialCode = item[0] || "";
                            if (item.length >= 2) specCode = item[1] || "";
                            if (item.length >= 3) description = item[2] || "";
                            if (item.length >= 4) classValue = item[3] || "";
                            if (item.length >= 5) size = item[4] || "";
                            if (item.length >= 6) beratKemasan = item[5] || "";
                        }

                        console.log("Extracted values:", {
                            materialCode,
                            specCode,
                            description,
                            classValue,
                            size,
                            beratKemasan
                        });

                        // Validasi: material_code wajib ada
                        if (!materialCode || materialCode.toString().trim() === "") {
                            console.log("Material Code kosong, diabaikan");
                            ignoredCount++;
                            continue;
                        }

                        const materialCodeLower = materialCode.toString().toLowerCase().trim();
                        
                        // Persiapkan data untuk disimpan
                        const dataToSave = {
                            material_code: materialCode.toString().trim(),
                            spec_code: specCode.toString().trim(),
                            description: description.toString().trim(),
                            class: classValue.toString().trim(),
                            size: size.toString().trim(),
                            berat_kemasan: beratKemasan.toString().trim(),
                            last_updated: new Date().toISOString()
                        };

                        console.log("Data yang akan disimpan:", dataToSave);

                        // Cek apakah material_code sudah ada
                        const existingKey = Object.keys(existingData).find(key => 
                            existingData[key].material_code?.toLowerCase().trim() === materialCodeLower
                        );

                        if (existingKey) {
                            // Update data yang sudah ada
                            const existingItemRef = ref(database, `${dbPath}/${existingKey}`);
                            await set(existingItemRef, dataToSave);
                            updatedCount++;
                            console.log(`Data diperbarui: ${materialCode}`);
                        } else {
                            // Simpan sebagai data baru
                            // Gunakan material_code sebagai key
                            const newRef = ref(database, `${dbPath}/${materialCode.toString().trim()}`);
                            await set(newRef, dataToSave);
                            importedCount++;
                            console.log(`Data baru: ${materialCode}`);
                        }

                    } catch (error) {
                        console.error("Error processing row:", error, "Row data:", item);
                        errors.push(`Error pada baris: ${JSON.stringify(item)} - ${error.message}`);
                        ignoredCount++;
                    }
                }

                // Refresh data setelah import
                await refreshData();

                // Tampilkan hasil import
                let message = `Import selesai! ${importedCount} data baru, ${updatedCount} data diperbarui.`;
                let severity = "success";
                
                if (ignoredCount > 0) {
                    message += ` ${ignoredCount} baris diabaikan (data kosong/tidak valid).`;
                    severity = "warning";
                }
                
                if (errors.length > 0) {
                    console.error("Import errors:", errors);
                }

                setSnackbar({
                    open: true,
                    message: message,
                    severity: severity,
                });

                console.log(`Import summary: ${importedCount} baru, ${updatedCount} diperbarui, ${ignoredCount} diabaikan`);

            } catch (error) {
                console.error("Gagal mengimpor Excel:", error);
                setSnackbar({
                    open: true,
                    message: `Gagal membaca file Excel: ${error.message}`,
                    severity: "error",
                });
            } finally {
                setImportLoading(false);
            }
        };

        reader.onerror = (error) => {
            console.error("Error reading file:", error);
            setSnackbar({
                open: true,
                message: "Gagal membaca file.",
                severity: "error",
            });
            setImportLoading(false);
        };

        reader.readAsArrayBuffer(file);

    } catch (error) {
        console.error("Error in import process:", error);
        setSnackbar({
            open: true,
            message: "Terjadi kesalahan saat memproses file.",
            severity: "error",
        });
        setImportLoading(false);
    } finally {
        event.target.value = null;
    }
};

    // Fungsi untuk refresh data dari Firebase (sama seperti di UoMConfig)
    const refreshData = async () => {
        try {
            setLoadingData(true);
            const masterDataRef = ref(database, "db_productions/tb_master_data");
            const masterDataSnapshot = await onValuePromise(masterDataRef);
            const masterData = masterDataSnapshot.val();
            const loadedData = [];
            let latestUpdateTime = null;
            
            if (masterData) {
                Object.keys(masterData).forEach(key => {
                    const record = masterData[key];
                    loadedData.push({ 
                        firebaseKey: key, 
                        material_code: record.material_code || '',
                        spec_code: record.spec_code || '',
                        description: record.description || '',
                        class: record.class || '',
                        size: record.size || '',
                        berat_kemasan: record.berat_kemasan || '',
                        last_updated: record.last_updated || null
                    });
                    
                    if (record.last_updated) {
                        const updateTime = new Date(record.last_updated);
                        if (!latestUpdateTime || updateTime > latestUpdateTime) {
                            latestUpdateTime = updateTime;
                        }
                    }
                });
                
                if (latestUpdateTime) {
                    setLastUpdated(latestUpdateTime);
                }
            }

            loadedData.sort((a, b) => a.material_code.localeCompare(b.material_code));
            
            setOriginalDataList(loadedData);
            setDataList(loadedData);
            
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        } finally {
            setLoadingData(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (row) => {
        setEditingRow(row.firebaseKey);
        setEditFormData({
            material_code: row.material_code,
            spec_code: row.spec_code,
            description: row.description,
            class: row.class,
            size: row.size,
            berat_kemasan: row.berat_kemasan
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = async (firebaseKey) => {
        try {
            // Update data di Firebase
            const dataRef = ref(database, `db_productions/tb_master_data/${firebaseKey}`);
            const updatedData = {
                ...editFormData,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, updatedData);
            
            // Update local state
            const updatedList = dataList.map(item => {
                if (item.firebaseKey === firebaseKey) {
                    return {
                        ...item,
                        ...updatedData,
                        last_updated: updatedData.last_updated
                    };
                }
                return item;
            });
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            setEditingRow(null);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diperbarui.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error updating data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memperbarui data.",
                severity: "error",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleDelete = async (firebaseKey, materialCode) => {
        if (window.confirm(`Apakah Anda yakin ingin menghapus data dengan Material Code: ${materialCode}?`)) {
            try {
                // Hapus data dari Firebase
                const dataRef = ref(database, `db_productions/tb_master_data/${firebaseKey}`);
                await remove(dataRef);
                
                // Update local state
                const updatedList = dataList.filter(item => item.firebaseKey !== firebaseKey);
                setDataList(updatedList);
                setOriginalDataList(updatedList);
                
                setSnackbar({
                    open: true,
                    message: "Data berhasil dihapus.",
                    severity: "success",
                });
                
                // Update last updated time
                setLastUpdated(new Date());
                
            } catch (error) {
                console.error("Error deleting data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal menghapus data.",
                    severity: "error",
                });
            }
        }
    };

    const handleAddNew = () => {
        setIsAddIconRotated(!isAddIconRotated);
        setShowAddForm(!showAddForm);
    };

    const handleNewDataChange = (field, value) => {
        setNewData({
            ...newData,
            [field]: value
        });
    };

    const handleSaveNew = async () => {
        // Validasi
        if (!newData.material_code.trim()) {
            setSnackbar({
                open: true,
                message: "Material Code tidak boleh kosong.",
                severity: "warning",
            });
            return;
        }

        try {
            // Cek apakah material_code sudah ada
            const existingData = originalDataList.find(item => 
                item.material_code === newData.material_code
            );
            
            if (existingData) {
                setSnackbar({
                    open: true,
                    message: "Material Code sudah ada dalam database.",
                    severity: "warning",
                });
                return;
            }

            // Simpan ke Firebase dengan key = material_code
            const dataRef = ref(database, `db_productions/tb_master_data/${newData.material_code}`);
            const dataToSave = {
                ...newData,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, dataToSave);
            
            // Update local state
            const newItem = {
                firebaseKey: newData.material_code,
                ...dataToSave
            };
            
            const updatedList = [...originalDataList, newItem].sort((a, b) => 
                a.material_code.localeCompare(b.material_code)
            );
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            
            // Reset form dan state
            setNewData({
                material_code: '',
                spec_code: '',
                description: '',
                class: '',
                size: '',
                berat_kemasan: ''
            });
            setShowAddForm(false);
            setIsAddIconRotated(false);
            
            setSnackbar({
                open: true,
                message: "Data berhasil ditambahkan.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error adding data:", error);
            setSnackbar({
                open: true,
                message: "Gagal menambahkan data.",
                severity: "error",
            });
        }
    };

    const handleCancelNew = () => {
        setShowAddForm(false);
        setIsAddIconRotated(false);
        setNewData({
            material_code: '',
            spec_code: '',
            description: '',
            class: '',
            size: '',
            berat_kemasan: ''
        });
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Master Data
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        {/* IconButton Plus/X untuk Tambah Data */}
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleAddNew}
                            sx={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: isAddIconRotated ? 'rotate(45deg)' : 'rotate(0deg)',
                                backgroundColor: isAddIconRotated ? '#f2f9ff' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isAddIconRotated ? '#f2f9ff' : 'action.hover'
                                }
                            }}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Form Tambah Data Baru */}
                {showAddForm && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f2f9ff' }} elevation={0}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Tambah Data Baru
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                            <TextField
                                label="Material Code"
                                size="small"
                                value={newData.material_code}
                                onChange={(e) => handleNewDataChange('material_code', e.target.value)}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Spec Code"
                                size="small"
                                value={newData.spec_code}
                                onChange={(e) => handleNewDataChange('spec_code', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Description"
                                size="small"
                                value={newData.description}
                                onChange={(e) => handleNewDataChange('description', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Class"
                                size="small"
                                value={newData.class}
                                onChange={(e) => handleNewDataChange('class', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Size"
                                size="small"
                                value={newData.size}
                                onChange={(e) => handleNewDataChange('size', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Berat Kemasan"
                                size="small"
                                value={newData.berat_kemasan}
                                onChange={(e) => handleNewDataChange('berat_kemasan', e.target.value)}
                                fullWidth
                            />
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" onClick={handleCancelNew}>
                                Batal
                            </Button>
                            <Button variant="contained" onClick={handleSaveNew}>
                                Simpan
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="master data table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="ded-tch">Material Code</TableCell>
                                        <TableCell className="ded-tch">Spec Code</TableCell>
                                        <TableCell className="ded-tch">Description</TableCell>
                                        <TableCell className="ded-tch">Class</TableCell>
                                        <TableCell className="ded-tch">Size</TableCell>
                                        <TableCell className="ded-tch">Berat Kemasan</TableCell>
                                        <TableCell className="ded-tch" style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.firebaseKey}>
                                                {editingRow === row.firebaseKey ? (
                                                    <>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.material_code}
                                                                disabled // Kunci input Material Code saat edit
                                                                fullWidth
                                                                sx={{
                                                                    '& .MuiInputBase-input.Mui-disabled': {
                                                                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                                                        backgroundColor: 'action.disabledBackground'
                                                                    }
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.spec_code}
                                                                onChange={(e) => handleEditChange('spec_code', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.description}
                                                                onChange={(e) => handleEditChange('description', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.class}
                                                                onChange={(e) => handleEditChange('class', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.size}
                                                                onChange={(e) => handleEditChange('size', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.berat_kemasan}
                                                                onChange={(e) => handleEditChange('berat_kemasan', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="success"
                                                                    onClick={() => handleSaveEdit(row.firebaseKey)}
                                                                >
                                                                    <CheckOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    <CloseOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {formatText(row.material_code)}
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{formatText(row.spec_code)}</TableCell>
                                                        <TableCell>{formatText(row.description)}</TableCell>
                                                        <TableCell>{formatText(row.class)}</TableCell>
                                                        <TableCell>{formatText(row.size)}</TableCell>
                                                        <TableCell>{formatText(row.berat_kemasan)}</TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="primary"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={() => handleDelete(row.firebaseKey, row.material_code)}
                                                                >
                                                                    <DeleteOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data master. Klik icon "+" untuk menambahkan atau "Import Excel" untuk mengimpor data.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MasterData;